import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Collapse,
  CircularProgress,
  Chip,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PlanStep } from '../../../../types/generated/graphql';
import { colors } from '../../../../theme/colors';
import StepInteractionFeed, { FeedItem } from '../StepInteractionFeed';

interface StepChatProps {
  step: PlanStep;
  feedItems: FeedItem[];
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
}

/**
 * Unified chat section for step-specific conversations.
 * 
 * - Empty state: "Need help with this step?" with input + quick questions
 * - With messages: "Step Chat (X)" with full history + input
 * - Collapsible to minimize when not needed
 */
export const StepChat: React.FC<StepChatProps> = ({
  step,
  feedItems,
  onSendMessage,
  isLoading = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState('');

  // Filter to only conversation messages
  const conversationItems = useMemo(() => {
    return feedItems.filter(item =>
      item.type === 'user_message' ||
      item.type === 'assistant_message' ||
      item.type === 'participant_message'
    );
  }, [feedItems]);

  const messageCount = conversationItems.length;
  const hasMessages = messageCount > 0;

  // Auto-expand when first message arrives
  React.useEffect(() => {
    if (hasMessages && !expanded) {
      setExpanded(true);
    }
  }, [hasMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = message.trim();
    if (trimmed && !isLoading) {
      // Include step context so AI knows what we're asking about
      const contextualMessage = `[Step: ${step.title}]\n\n${trimmed}`;
      onSendMessage(contextualMessage);
      setMessage('');
    }
  }, [message, isLoading, onSendMessage, step.title]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleQuickQuestion = (q: string) => {
    if (!isLoading) {
      setMessage(q);
    }
  };

  // Generate quick questions based on step
  const quickQuestions = useMemo(() => getQuickQuestions(step), [step]);

  // Header styling based on state
  const headerIcon = hasMessages ? ChatIcon : HelpOutlineIcon;
  const HeaderIcon = headerIcon;
  const headerColor = colors.purple[600];
  const headerText = hasMessages
    ? `Step Chat (${messageCount})`
    : 'Need help with this step?';

  return (
    <Box sx={{ borderTop: `1px solid ${colors.gray[200]}` }}>
      {/* Collapsible Header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: colors.gray[50],
          },
          transition: 'background-color 0.15s ease',
        }}
      >
        <HeaderIcon sx={{ fontSize: 18, color: headerColor }} />
        <Typography
          variant="body2"
          sx={{
            color: headerColor,
            fontWeight: 500,
            flex: 1,
          }}
        >
          {headerText}
        </Typography>
        {hasMessages && (
          <Chip
            label={messageCount}
            size="small"
            sx={{
              height: 20,
              minWidth: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: colors.purple[100],
              color: colors.purple[600],
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: colors.gray[400],
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Conversation History */}
          {hasMessages && (
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <StepInteractionFeed items={feedItems} stepTitle={step.title} />
            </Box>
          )}

          {/* Input Section */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: hasMessages ? `1px solid ${colors.gray[200]}` : 'none',
              bgcolor: colors.gray[50],
            }}
          >
            {/* Quick Questions - show when no active input */}
            {!message && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {quickQuestions.map((q) => (
                  <Typography
                    key={q}
                    variant="caption"
                    onClick={() => handleQuickQuestion(q)}
                    sx={{
                      color: colors.purple[600],
                      cursor: isLoading ? 'default' : 'pointer',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: 'white',
                      border: `1px solid ${colors.purple[100]}`,
                      '&:hover': {
                        bgcolor: isLoading ? 'white' : colors.purple[50],
                        borderColor: isLoading ? colors.purple[100] : colors.purple[500],
                      },
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {q}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Message Input */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
              }}
            >
              <TextField
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={getPlaceholder(step)}
                multiline
                maxRows={3}
                fullWidth
                size="small"
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: 2,
                    fontSize: '0.875rem',
                    '& fieldset': {
                      borderColor: colors.gray[300],
                    },
                    '&:hover fieldset': {
                      borderColor: colors.purple[500],
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.purple[500],
                    },
                  },
                }}
              />
              <IconButton
                type="submit"
                disabled={!message.trim() || isLoading}
                size="small"
                sx={{
                  bgcolor: message.trim() ? colors.purple[600] : colors.gray[200],
                  color: message.trim() ? 'white' : colors.gray[400],
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  '&:hover': {
                    bgcolor: message.trim() ? colors.purple[500] : colors.gray[200],
                  },
                  '&.Mui-disabled': {
                    bgcolor: colors.gray[200],
                    color: colors.gray[400],
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Generate contextual placeholder text based on step type.
 */
function getPlaceholder(step: PlanStep): string {
  const title = step.title.toLowerCase();

  if (title.includes('measure') || title.includes('ingredient')) {
    return 'e.g., "What\'s the exact amount?"';
  }
  if (title.includes('warm') || title.includes('heat') || title.includes('temperature')) {
    return 'e.g., "What temperature?"';
  }
  if (title.includes('mix') || title.includes('combine')) {
    return 'e.g., "How long should I mix?"';
  }
  if (title.includes('wait') || title.includes('proof') || title.includes('rise')) {
    return 'e.g., "How do I know when ready?"';
  }

  return 'Ask a question about this step...';
}

/**
 * Generate quick question suggestions based on step content.
 */
function getQuickQuestions(step: PlanStep): string[] {
  const title = step.title.toLowerCase();
  const description = (step.description || '').toLowerCase();
  const combined = `${title} ${description}`;

  const questions: string[] = [];

  if (combined.includes('warm') || combined.includes('heat') || combined.includes('temperature')) {
    questions.push('What temperature?');
  }
  if (combined.includes('wait') || combined.includes('proof') || combined.includes('rise') || combined.includes('rest')) {
    questions.push('How long?');
  }
  if (combined.includes('measure') || combined.includes('cup') || combined.includes('tbsp')) {
    questions.push('Exact amounts?');
  }
  if (combined.includes('check') || combined.includes('verify') || combined.includes('confirm')) {
    questions.push('How to verify?');
  }

  // Add generic questions
  if (questions.length < 3) {
    questions.push('Explain this');
  }
  if (questions.length < 3) {
    questions.push('What could go wrong?');
  }

  return questions.slice(0, 3);
}

export default StepChat;

